import { animate } from "https://cdn.jsdelivr.net/npm/motion@12.40.0/+esm";

const revealSelector = '[data-animate="reveal"]';
const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
let revealObserver;
let revealFrameRequested = false;
let revealWatchTimer;
let revealWatchCount = 0;

function toNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function revealNow(element) {
  element.style.opacity = "1";
  element.style.transform = "none";
  element.style.willChange = "";
}

function revealWithMotion(element) {
  if (element.dataset.animateRevealed === "true") return;

  element.dataset.animateRevealed = "true";
  if (revealObserver) revealObserver.unobserve(element);

  const delay = toNumber(element.dataset.animateDelay, 0);
  const duration = toNumber(element.dataset.animateDuration, 0.55);

  animate(
    element,
    { opacity: 1, transform: "translate3d(0px, 0px, 0) scale(1)" },
    { delay, duration, ease: [0.22, 1, 0.36, 1] }
  );

  window.setTimeout(() => {
    element.style.opacity = "1";
    element.style.transform = "translate3d(0px, 0px, 0) scale(1)";
    element.style.willChange = "";
  }, (delay + duration) * 1000 + 100);
}

function getRevealObserver() {
  if (revealObserver) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        revealWithMotion(entry.target);
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: "0px 0px 12% 0px", threshold: 0.01 }
  );

  return revealObserver;
}

function isInRevealRange(element) {
  const rect = element.getBoundingClientRect();
  return rect.top <= window.innerHeight * 1.12 && rect.bottom >= 0;
}

function checkRevealPositions() {
  revealFrameRequested = false;
  revealWatchCount += 1;

  document.querySelectorAll(`${revealSelector}[data-animate-ready="true"]`).forEach((element) => {
    if (!(element instanceof HTMLElement) || element.dataset.animateRevealed === "true") return;
    if (isInRevealRange(element)) revealWithMotion(element);
  });

  if (
    revealWatchCount >= 80 ||
    !document.querySelector(`${revealSelector}[data-animate-ready="true"]:not([data-animate-revealed="true"])`)
  ) {
    window.clearInterval(revealWatchTimer);
    revealWatchTimer = undefined;
    revealWatchCount = 0;
  }
}

function requestRevealCheck() {
  if (revealFrameRequested) return;

  revealFrameRequested = true;
  window.setTimeout(checkRevealPositions, 16);
}

function startRevealWatch() {
  if (revealWatchTimer || reduceMotionQuery.matches) return;
  revealWatchCount = 0;
  revealWatchTimer = window.setInterval(checkRevealPositions, 250);
}

function setupRevealElement(element) {
  if (!(element instanceof HTMLElement) || element.dataset.animateReady === "true") return;

  element.dataset.animateReady = "true";

  if (reduceMotionQuery.matches) {
    revealNow(element);
    return;
  }

  const y = toNumber(element.dataset.animateY, 18);
  const x = toNumber(element.dataset.animateX, 0);
  const scale = toNumber(element.dataset.animateScale, 1);

  element.style.opacity = "0";
  element.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  element.style.willChange = "opacity, transform";

  if ("IntersectionObserver" in window) getRevealObserver().observe(element);
  requestRevealCheck();
  startRevealWatch();
}

function setupReveal(root = document) {
  if (root instanceof HTMLElement && root.matches(revealSelector)) {
    setupRevealElement(root);
  }

  root.querySelectorAll(revealSelector).forEach(setupRevealElement);
}

function revealAll() {
  document.querySelectorAll(revealSelector).forEach((element) => {
    if (revealObserver) revealObserver.unobserve(element);
    revealNow(element);
  });
  window.clearInterval(revealWatchTimer);
  revealWatchTimer = undefined;
  revealWatchCount = 0;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => setupReveal());
} else {
  setupReveal();
}

document.addEventListener("shopify:section:load", (event) => setupReveal(event.target));
window.addEventListener("scroll", requestRevealCheck, { passive: true });
document.addEventListener("scroll", requestRevealCheck, { passive: true });
window.addEventListener("resize", requestRevealCheck);

function handleReducedMotionChange() {
  if (reduceMotionQuery.matches) revealAll();
}

if (typeof reduceMotionQuery.addEventListener === "function") {
  reduceMotionQuery.addEventListener("change", handleReducedMotionChange);
} else if (typeof reduceMotionQuery.addListener === "function") {
  reduceMotionQuery.addListener(handleReducedMotionChange);
}

new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) setupReveal(node);
    });
  });
}).observe(document.documentElement, { childList: true, subtree: true });
